import { useState } from "react";
import { HelpCircle, ShieldCheck } from "lucide-react";
import { useTranslations } from "use-intl";
import { Button } from "@/components/ui/button";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@/components/ui/modal";
import { cn } from "@/utils";

type PermissionHelpButtonProps = {
  className?: string;
  label?: string;
};

const conceptKeys = [
  "owner",
  "direct",
  "folder",
  "default",
  "publicLink",
  "admin",
] as const;

export function PermissionHelpButton({ className, label }: PermissionHelpButtonProps) {
  const t = useTranslations("permissions.help");
  const [open, setOpen] = useState(false);
  const buttonLabel = label ?? t("button");

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium",
          "text-[var(--muted-foreground)] hover:bg-[var(--accent)] hover:text-[var(--foreground)]",
          "focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus-ring)]",
          className
        )}
        aria-label={buttonLabel}
        title={buttonLabel}
      >
        <HelpCircle className="size-3.5" aria-hidden />
        {label && <span>{label}</span>}
      </button>

      <Modal isOpen={open} onClose={() => setOpen(false)} size="lg" aria-label={t("title")}>
        <ModalHeader>
          <div className="flex items-center gap-3">
            <span className="flex size-9 items-center justify-center rounded-lg bg-[var(--accent)] text-[var(--foreground)]">
              <ShieldCheck className="size-4" aria-hidden />
            </span>
            <div>
              <h2 className="text-[15px] font-semibold text-[var(--foreground)]">{t("title")}</h2>
              <p className="mt-0.5 text-xs text-[var(--muted-foreground)]">{t("subtitle")}</p>
            </div>
          </div>
        </ModalHeader>
        <ModalBody className="space-y-3">
          {conceptKeys.map((key) => (
            <section key={key} className="rounded-lg border border-[var(--border)] p-3">
              <h3 className="text-sm font-semibold text-[var(--foreground)]">
                {t(`concepts.${key}.title`)}
              </h3>
              <p className="mt-1 text-xs leading-5 text-[var(--muted-foreground)]">
                {t(`concepts.${key}.body`)}
              </p>
            </section>
          ))}
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t("close")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default PermissionHelpButton;
